/**
 * What each id in a filter names — the line beneath the Advanced tab's text
 * (deterministic filters, owner ruling E10: "show the saved text, with a line
 * beneath naming each id"; docs/plans/deterministic-filters.md §3.7).
 *
 * A filter names keepr's things by id since F2: a card (`card = <id>`, a
 * facet `@<id>`), an element set, a collection, a record a lookup points at, a
 * person, a tag — and, since element ids (KPR-168), an element (`#k7f3q2xa`,
 * and in a reference, `{{#k7f3q2xa}}`). The text says exactly what is stored; this says what it is in
 * words, from what the page already holds:
 *   - cards, sets and collections from the field vocabulary (their choices);
 *   - records and people from `titleFor` (useQueryLookupTitles);
 *   - tags from the tags field's own vocabulary.
 * An id the page cannot name is said so — `label: null` — never guessed: it
 * may be a card taken off this collection, another collection's, or one the
 * reader cannot see. One the server marked removed (`removedIds`, from
 * X-Query-Warnings) reads "a removed …".
 */
import { parse, isElementIdSegment } from './kql';
import { GONE_ELEMENT_LABEL, conditionValueLabel, elementIdSegmentLabel, fieldKindOf, findField } from './kqlBuilder';
import { isFacetField } from './facets';
import { TAG_FIELD_KEY, UNKNOWN_TAG_LABEL } from './tagNames';

const HEX = /^[0-9a-f]{24}$/i;
const isHex = value => HEX.test(String(value || '').trim());

const REFERENCE_NOUNS = { card: 'card', set: 'element set' };

/** The noun for what an id in this field names. */
function nounFor(field) {
	if (!field) return 'record';
	if (field.reference) return REFERENCE_NOUNS[field.reference] || 'record';
	if (field.key === TAG_FIELD_KEY || fieldKindOf(field) === 'tag') return 'tag';
	if (field.key === 'collection') return 'collection';
	if (field.dataType === 'user') return 'person';
	return 'record';
}

/** The words for one id in one field, or null when the page cannot name it. */
function labelFor(field, id, titleFor) {
	if (!field || field.unknown) return null;
	const kind = fieldKindOf(field);
	if (field.reference || kind === 'choice') {
		const label = conditionValueLabel(field, id);
		return label === undefined ? null : label;
	}
	if (field.key === TAG_FIELD_KEY || kind === 'tag') {
		const label = conditionValueLabel(field, id);
		return label === undefined || label === UNKNOWN_TAG_LABEL ? null : label;
	}
	const title = typeof titleFor === 'function' ? titleFor(id) : undefined;
	return title === undefined ? null : title;
}

/**
 * `[{ id, label, noun }]`: each id `text` names, in the order it first appears,
 * once per noun. `label` is null for an id the page cannot name. Text that
 * does not parse names nothing.
 */
export function idsNamedIn(text, fields = [], { titleFor = null, removedIds = null, unknownElement = null } = {}) {
	const trimmed = (text || '').trim();
	if (!trimmed) return [];
	let ast;
	try { ast = parse(trimmed); } catch (err) { return []; }
	const removed = removedIds instanceof Set ? removedIds : new Set(removedIds || []);
	const out = [];
	const seen = new Set();
	const add = (id, label, noun) => {
		const key = `${noun}:${String(id).toLowerCase()}`;
		if (seen.has(key)) return;
		seen.add(key);
		// An element id nothing here holds says so in words (and takes no noun
		// after it) — "no longer here" only where the fields hold every card
		// the text reaches; else the caller's own words (`unknownElement`):
		// an element is gone only when what could hold it was looked at (the
		// step 2 review's EK-W1; the P4 review's R-WL1).
		if (noun === 'element' && label === null) {
			out.push({ id: String(id), label: unknownElement || GONE_ELEMENT_LABEL.toLowerCase(), noun, removed: true });
			return;
		}
		const gone = removed.has(String(id).toLowerCase());
		out.push({ id: String(id).toLowerCase(), label: gone ? `a removed ${noun}` : label, noun, ...(gone ? { removed: true } : {}) });
	};
	const walk = (node) => {
		if (!node || typeof node !== 'object') return;
		if (Array.isArray(node.args)) { node.args.forEach(walk); return; }
		if (node.arg) { walk(node.arg); return; }
		if (node.op !== 'cmp' || !Array.isArray(node.path)) return;
		const field = findField(fields, node.path, !!node.forceElement, !!node.facet);
		// An element named by its id (`#k7f3q2xa`, KPR-168): its label — a
		// hop's later segment as the element the hop walks into (review
		// EK-W1) — or, when nothing here holds the id, that it is no longer here.
		if (!node.facet) {
			node.path.forEach((segment, i) => {
				if (!isElementIdSegment(segment)) return;
				add(segment, elementIdSegmentLabel(fields, node.path, i), 'element');
			});
		}
		// A facet names a CARD by its path.
		if (node.facet && isHex(node.path[0])) {
			add(node.path[0], field && !field.unknown && isFacetField(field) ? (field.shortLabel || field.label) : null, 'card');
		}
		const value = node.value;
		if (!value) return;
		const values = value.type === 'list' ? (value.v || []) : [value];
		// A reference by id (`{{#k7f3q2xa}}`, element ids step 3's readers): an
		// element of the same record, named as a root id is.
		values.forEach((entry) => {
			if (entry && entry.type === 'ref' && isElementIdSegment(entry.v)) add(entry.v, elementIdSegmentLabel(fields, [entry.v], 0), 'element');
		});
		values.forEach((entry) => {
			if (!entry || entry.type !== 'str' || !isHex(entry.v)) return;
			add(entry.v, labelFor(field, String(entry.v).trim(), titleFor), nounFor(field));
		});
	};
	walk(ast);
	return out;
}
